import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { analyticsService } from '../services/analyticsService';

const FinanceAnalytics = ({ filters = {} }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthlyEvolutionData, setMonthlyEvolutionData] = useState([]);
  const [productMarginData, setProductMarginData] = useState([]);
  const [verificationKPIs, setVerificationKPIs] = useState({});

  useEffect(() => {
    loadAnalytics();
  }, [filters]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const [monthlyEvolution, productMargin, kpis] = await Promise.all([
        analyticsService.getFinanceMonthlyEvolution(filters),
        analyticsService.getFinanceProductMargin(filters),
        analyticsService.getFinanceVerificationKPIs(filters)
      ]);

      console.log('Finance Analytics Debug:', {
        monthlyEvolution,
        productMargin,
        kpis
      });

      // The API returns data directly, not wrapped in a data property
      setMonthlyEvolutionData(Array.isArray(monthlyEvolution) ? monthlyEvolution : (monthlyEvolution.data || []));
      setProductMarginData(Array.isArray(productMargin) ? productMargin : (productMargin.data || []));
      setVerificationKPIs(typeof kpis === 'object' && !Array.isArray(kpis) ? kpis : (kpis.data || {}));

    } catch (err) {
      console.error('Erro ao carregar analytics financeiros:', err);
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format((value || 0) / 100);
  };

  const formatClassification = (classification) => {
    const labels = {
      'custo_fixo': 'Custo Fixo',
      'custo_variavel': 'Custo Variável',
      'despesa_fixa': 'Despesa Fixa',
      'despesa_variavel': 'Despesa Variável',
      'receita': 'Receita',
      'imposto': 'Imposto',
      'retirada_lucros': 'Retirada lucros',
      'ativo_imobilizado': 'Ativo imobilizado'
    };
    return labels[classification] || classification;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* KPIs de Verificação */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Total de Transações
                </Typography>
                <Typography variant="h4" fontWeight="700">
                  {verificationKPIs.total_transactions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Transações Verificadas
                </Typography>
                <Typography variant="h4" fontWeight="700" color="success.main">
                  {verificationKPIs.verified_transactions || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  % Não Verificado
                </Typography>
                <Typography variant="h4" fontWeight="700" color="warning.main">
                  {formatPercentage(verificationKPIs.unverified_percentage)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Tempo Médio Verificação
                </Typography>
                <Typography variant="h4" fontWeight="700" color="info.main">
                  {(verificationKPIs.average_verification_time_hours || 0).toFixed(1)}h
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Evolução Mensal por Classificação */}
      <Grid item xs={12} lg={8}>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Evolução Mensal por Classificação
            </Typography>
            <Box sx={{ height: 400, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyEvolutionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="period" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { 
                      month: 'short', 
                      year: '2-digit' 
                    })}
                  />
                  <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), formatClassification(name)]}
                    labelFormatter={(label) => new Date(label).toLocaleDateString('pt-BR', { 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  />
                  <Legend formatter={(value) => formatClassification(value)} />
                  {Array.from(new Set(monthlyEvolutionData.map(d => d.classification))).map((classification, index) => (
                    <Line 
                      key={classification}
                      type="monotone"
                      dataKey="total_amount"
                      data={monthlyEvolutionData.filter(d => d.classification === classification)}
                      stroke={[
                        '#1976d2', // Receita - azul
                        '#2e7d32', // Custo variável - verde
                        '#ed6c02', // Custo fixo - laranja
                        '#9c27b0', // Despesa variável - roxo
                        '#d32f2f'  // Despesa fixa - vermelho
                      ][index]}
                      name={classification}
                      strokeWidth={2}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Margem por Produto (Ranking) */}
      <Grid item xs={12} lg={4}>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ranking de Margem por Produto
            </Typography>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell align="right">Margem</TableCell>
                    <TableCell align="right">Lucro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productMarginData.map((product, index) => (
                    <TableRow key={product.product_id || index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {product.product_id || `Produto ${index + 1}`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatPercentage(product.profit_margin)}
                          color={
                            product.profit_margin > 20 ? 'success' :
                            product.profit_margin > 10 ? 'warning' : 'error'
                          }
                          size="small"
                          sx={{ minWidth: 60 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          color={product.net_profit >= 0 ? 'success.main' : 'error.main'}
                          sx={{ fontSize: '0.8rem' }}
                        >
                          {formatCurrency(product.net_profit)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Detalhes dos Produtos com Melhor Margem */}
      {productMarginData.length > 0 && (
        <Grid item xs={12}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Detalhamento Financeiro dos Produtos
              </Typography>
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={productMarginData.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="product_id" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                    />
                    <Legend />
                    <Bar dataKey="total_revenue" fill="#2e7d32" name="Receita" />
                    <Bar dataKey="total_costs" fill="#ed6c02" name="Custos" />
                    <Bar dataKey="total_expenses" fill="#d32f2f" name="Despesas" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default FinanceAnalytics;